import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faCopy, faCheck, faLock, faExclamationTriangle, faCircleInfo, faUndo, faCamera, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import PhoneInput from "../ui/PhoneInput";
import { uploadProfilePic, removeProfilePic } from "../../api/uploadApi";
import config from "../../config";

function GeneralSection({ profile, formData, onChange, onSave, onCancel, isDirty, darkMode, t, getFieldCooldown, cooldownDays, onProfilePicChange }) {
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [picLoading, setPicLoading] = useState(false);
  const fileRef = useRef(null);

  const emailCooldown = getFieldCooldown?.("email");

  const policyTooltip = t("To prevent fraud, your email address can only be changed once every {{days}} days. Previous values are logged for security purposes.", { days: cooldownDays || 60 });

  const isEmailChanged = formData.email !== (profile?.email || "");
  const isPhoneChanged = formData.phone_number !== (profile?.phone_number || "");

  const copyId = () => {
    if (profile?.id) {
      navigator.clipboard.writeText(profile.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const save = async () => {
    setSaving(true);
    setAlert(null);
    const result = await onSave();
    setSaving(false);
    if (result.success) {
      setAlert({ type: "success", msg: t("Changes saved") });
    } else {
      setAlert({ type: "error", msg: result.errors?.[0] || t("Failed to save") });
    }
    setTimeout(() => setAlert(null), 4000);
  };

  const truncate = (id) => {
    if (!id || id.length < 16) return id || "";
    return `${id.slice(0, 8)}...${id.slice(-6)}`;
  };

  const resetField = (field) => {
    onChange(field, profile?.[field] || "");
  };

  const handlePicSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPicLoading(true);
    setAlert(null);
    try {
      const res = await uploadProfilePic(file);
      const resolved = res.url?.startsWith("/api/") ? `${config.apiUrl}${res.url.slice(4)}` : res.url;
      const busted = `${resolved}?t=${Date.now()}`;
      onProfilePicChange?.(busted);
      setLocalPicUrl(busted);
      setAlert({ type: "success", msg: t("Profile picture updated") });
    } catch {
      setAlert({ type: "error", msg: t("Failed to upload picture") });
    } finally {
      setPicLoading(false);
      e.target.value = "";
      setTimeout(() => setAlert(null), 4000);
    }
  };

  const handlePicRemove = async () => {
    setPicLoading(true);
    setAlert(null);
    try {
      await removeProfilePic();
      onProfilePicChange?.(null);
      setLocalPicUrl(null);
      setAlert({ type: "success", msg: t("Profile picture removed") });
    } catch {
      setAlert({ type: "error", msg: t("Failed to remove picture") });
    } finally {
      setPicLoading(false);
      setTimeout(() => setAlert(null), 4000);
    }
  };

  const [localPicUrl, setLocalPicUrl] = useState(profile?.profile_pic_url ?? null);
  const picUrl = localPicUrl;

  useEffect(() => {
    if (profile?.profile_pic_url !== undefined) setLocalPicUrl(profile.profile_pic_url);
  }, [profile?.profile_pic_url]);

  return (
    <div className="profile-section">
      <div className="profile-section-header">
        <div className="profile-section-icon">
          <FontAwesomeIcon icon={faUser} />
        </div>
        <div>
          <h2 className="profile-section-title">
            {t("General Information")}
            <Tooltip title={policyTooltip} placement="top" arrow>
              <span className="profile-policy-icon">
                <FontAwesomeIcon icon={faCircleInfo} />
              </span>
            </Tooltip>
          </h2>
          <p className="profile-section-desc">{t("Your personal account details")}</p>
        </div>
      </div>

      {/* avatar row */}
      <div className="profile-avatar-row">
        <div className="profile-avatar" onClick={() => !picLoading && fileRef.current?.click()}>
          {picUrl
            ? <img src={picUrl} alt="profile" className="profile-avatar-img" onError={e => { e.target.style.display = "none"; }} />
            : <FontAwesomeIcon icon={faUser} className="profile-avatar-placeholder" />
          }
          <div className="profile-avatar-overlay">
            <FontAwesomeIcon icon={faCamera} />
          </div>
          {picLoading && <div className="profile-avatar-spinner" />}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={handlePicSelect}
        />
        <div className="profile-avatar-actions">
          <button
            className="profile-btn profile-btn-secondary profile-btn-sm"
            onClick={() => fileRef.current?.click()}
            disabled={picLoading}
          >
            {t("Change photo")}
          </button>
          {picUrl && (
            <button
              className="profile-btn profile-btn-danger profile-btn-sm"
              onClick={handlePicRemove}
              disabled={picLoading}
            >
              <FontAwesomeIcon icon={faTrash} />
              {t("Remove")}
            </button>
          )}
        </div>
      </div>

      {alert && (
        <div className={`profile-alert ${alert.type}`}>
          <FontAwesomeIcon icon={alert.type === "success" ? faCheck : faExclamationTriangle} />
          {alert.msg}
        </div>
      )}

      <div className="profile-form-grid">
        <div className="profile-form-group">
          <label className="profile-label">{t("Account ID")}</label>
          <div className="profile-input-wrap">
            <input
              type="text"
              className="profile-input readonly"
              value={truncate(profile?.id)}
              readOnly
            />
            <span className="profile-input-icon" onClick={copyId} title={t("Copy")}>
              <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
            </span>
          </div>
        </div>

        <div className="profile-form-group">
          <label className="profile-label">
            {t("Email Address")}
            {emailCooldown && (
              <span className="profile-cooldown-badge">
                <FontAwesomeIcon icon={faLock} />
                {emailCooldown} {t("days")}
              </span>
            )}
          </label>
          <div className="profile-input-wrap">
            <input
              type="text"
              className={`profile-input ${emailCooldown ? "readonly" : ""} ${isEmailChanged ? "has-reset" : ""}`}
              value={formData.email || ""}
              onChange={(e) => onChange("email", e.target.value)}
              readOnly={!!emailCooldown}
            />
            {isEmailChanged && !emailCooldown && (
              <Tooltip title={t("Reset to original")} placement="top" arrow>
                <span className="profile-input-reset" onClick={() => resetField("email")}>
                  <FontAwesomeIcon icon={faUndo} />
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="profile-form-group">
          <label className="profile-label">{t("Phone Number")}</label>
          <div className="profile-input-wrap phone-input-with-reset">
            <PhoneInput
              value={formData.phone_number || ""}
              onChange={(val) => onChange("phone_number", val)}
            />
            {isPhoneChanged && (
              <Tooltip title={t("Reset to original")} placement="top" arrow>
                <span className="profile-input-reset phone-reset" onClick={() => resetField("phone_number")}>
                  <FontAwesomeIcon icon={faUndo} />
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {isDirty && (
        <div className="profile-btn-row">
          <button className="profile-btn profile-btn-secondary" onClick={onCancel}>
            {t("Cancel")}
          </button>
          <button className="profile-btn profile-btn-primary" onClick={save} disabled={saving}>
            <FontAwesomeIcon icon={faCheck} />
            {saving ? t("Saving...") : t("Save Changes")}
          </button>
        </div>
      )}
    </div>
  );
}

export default GeneralSection;
